<script setup>
import { ref, watchEffect } from 'vue';
import { router, useForm, usePage } from '@inertiajs/vue3';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import JetActionSection from '@/Jetstream/ActionSection.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue';
import JetDangerButton from '@/Jetstream/DangerButton.vue';
import JetDialogModal from '@/Jetstream/DialogModal.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetSectionBorder from '@/Jetstream/SectionBorder.vue';

// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';
// Get locale as set in cookie
let { locale } = useI18n({ useScope: 'global' });
// This needs removing before pushing.

const props = defineProps({
    team: Object,
    availableRoles: Array,
    userPermissions: Object,
    loggedInUserId: Number,
    loggedInUserRole: Object,
});

// Function to count the number of admins
const countAdmins = (teamUsers) => {
    return teamUsers.reduce((count, user) => {
        return user.membership.role === "admin" ? count + 1 : count;
    }, 0);
};

const teamUsers = props.team.users;
const adminCount = ref(countAdmins(teamUsers));

const loggedInUserRole = props.loggedInUserRole.role;
const usePageProps = usePage().props;
const loggedInUserId = usePage().props.loggedInUserId;
const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const urlNamePrefix = organisation ? "gi." : "";

let team_custom_name = programme.team_custom_name;
if (team_custom_name) {
    if(team_custom_name.constructor.name != 'Object') {
        team_custom_name = JSON.parse(team_custom_name);
    }
}

let default_team_name;

if (locale.value == 'cy') {
    default_team_name = 'tîm';

} else  {
    default_team_name = 'team';
}

let team_name = default_team_name;

if (team_custom_name) {
    team_name =  team_custom_name[locale.value] == null ? default_team_name : team_custom_name[locale.value];
}

const addTeamMemberForm = useForm({
    email: '',
    //role: null,
    role: 'member',
});

const updateRoleForm = useForm({
    role: null,
});

const leaveTeamForm = useForm({});
const removeTeamMemberForm = useForm({});

const currentlyManagingRole = ref(false);
const managingRoleFor = ref(null);
const confirmingLeavingTeam = ref(false);
const teamMemberBeingRemoved = ref(null);

const invalidUserErrorMessage = ref();

const addTeamMember = () => {

    addTeamMemberForm.post(route(urlNamePrefix+'team-members.store', props.team.id), {
        errorBag: 'addTeamMember',
        preserveScroll: true,
        //onSuccess: () => addTeamMemberForm.reset(),
        onSuccess: (response) => {
            invalidUserErrorMessage.value = '';

        },
        onError: (response) => {
            invalidUserErrorMessage.value = response.valid_user;
        }
    });
};

const cancelTeamInvitation = (invitation) => {
    router.delete(route(urlNamePrefix+'team-invitations.destroy', invitation), {
        preserveScroll: true,
    });
};

const manageRole = (teamMember) => {
    managingRoleFor.value = teamMember;
    updateRoleForm.role = teamMember.membership.role;
    currentlyManagingRole.value = true;
};

const updateRole = () => {
    updateRoleForm.put(route(urlNamePrefix+'team-members.update', [props.team, managingRoleFor.value]), {
        preserveScroll: true,
        onSuccess: () => {
            // Update the currentlyManagingRole state
            currentlyManagingRole.value = false;

            // Assuming 'data' is available here or passed as a parameter
            // Update the admin count after the role update is successful
            adminCount.value = countAdmins(props.team.users);
        },
    });
};

const confirmLeavingTeam = () => {
    confirmingLeavingTeam.value = true;
};

const leaveTeam = () => {
    leaveTeamForm.delete(route(urlNamePrefix+'team-members.destroy', [props.team, loggedInUserId]));
};

const confirmTeamMemberRemoval = (teamMember) => {
    teamMemberBeingRemoved.value = teamMember;
};

const removeTeamMember = () => {
    removeTeamMemberForm.delete(route(urlNamePrefix + 'team-members.destroy', [props.team, teamMemberBeingRemoved.value]), {
        errorBag: 'removeTeamMember',
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            teamMemberBeingRemoved.value = null;
            adminCount.value = countAdmins(props.team.users);
        },
    });
};

const displayableRole = (role) => {
    if (!role) {
        role = 'member';
    }
    return props.availableRoles.find(r => r.key === role).name;
};


if ( (props.loggedInUserId == props.team.owner.id) && props.loggedInUserRole == 'member' ) {
    //console.log("The owner is now a member so shouldn't have change user permissions");
    props.userPermissions.canAddTeamMembers = false;
    props.userPermissions.canRemoveTeamMembers = false;
}

</script>

<template>
    <div>
        <div v-if="userPermissions.canAddTeamMembers">
            <JetSectionBorder />

            <!-- Add Team Member -->
            <JetFormSection @submitted="addTeamMember">
                <template #title>
                    {{ $t('teams.members.add.title', { team_name: team_name }) }}
                </template>

                <template #description>
                    {{ $t('teams.members.add.description', { team_name: team_name }) }}
                </template>

                <template #form>
                    <div class="col-span-6">
                        <div class="max-w-xl text-sm text-gray-600">
                            {{ $t('teams.members.add.info', { team_name: team_name }) }}
                        </div>
                    </div>

                    <!-- Member Email -->
                    <div class="col-span-6 sm:col-span-4">
                        <p v-if=invalidUserErrorMessage class="text-sm text-red-600">{{ invalidUserErrorMessage }}</p>
                        <JetLabel for="email" :value="$t('teams.members.add.form.email')" />
                        <JetInput
                            id="email"
                            v-model="addTeamMemberForm.email"
                            type="email"
                            class="mt-1 block w-full"
                        />
                        <JetInputError :message="addTeamMemberForm.errors.email" class="mt-2" />
                    </div>

                    <!-- Role -->
                    <div v-if="availableRoles.length > 0" class="col-span-6 lg:col-span-4">
                        <JetLabel for="roles" :value="$t('teams.members.add.form.role')" />
                        <JetInputError :message="addTeamMemberForm.errors.role" class="mt-2" />

                        <div class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer">
                            <button
                                v-for="(role, i) in availableRoles"
                                :key="role.key"
                                type="button"
                                class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                                :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i != Object.keys(availableRoles).length - 1}"
                                @click="addTeamMemberForm.role = role.key"
                            >
                                <div :class="{'opacity-50': addTeamMemberForm.role && addTeamMemberForm.role != role.key}">
                                    <!-- Role Name -->
                                    <div class="flex items-center">
                                        <div class="text-sm text-gray-600" :class="{'font-semibold': addTeamMemberForm.role == role.key}">
                                            {{ role.name }}
                                        </div>

                                        <svg
                                            v-if="addTeamMemberForm.role == role.key"
                                            class="ml-2 h-5 w-5 text-green-400"
                                            fill="none"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        ><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    </div>

                                    <!-- Role Description -->
                                    <div class="mt-2 text-xs text-gray-600 text-left">
                                        {{ role.description }}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </template>

                <template #actions>
                    <JetActionMessage :on="addTeamMemberForm.recentlySuccessful" class="mr-3">
                        {{ $t('teams.members.add.form.success') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': addTeamMemberForm.processing }" :disabled="addTeamMemberForm.processing">
                        {{ $t('teams.members.add.form.submit') }}
                    </JetButton>
                </template>
            </JetFormSection>
        </div>

        <div v-if="team.team_invitations.length > 0 && userPermissions.canAddTeamMembers">
            <JetSectionBorder />

            <!-- Team Member Invitations -->
            <JetActionSection class="mt-10 sm:mt-0">
                <template #title>
                    {{ $t('teams.members.pending.title') }}
                </template>

                <template #description>
                    {{ $t('teams.members.pending.description') }}
                </template>

                <!-- Pending Team Member Invitation List -->
                <template #content>
                    <div class="space-y-6">
                        <div v-for="invitation in team.team_invitations" :key="invitation.id" class="flex items-center justify-between">
                            <div class="text-gray-600">
                                {{ invitation.email }}
                            </div>

                            <div class="flex items-center">
                                <!-- Cancel Team Invitation -->
                                <button
                                    v-if="userPermissions.canRemoveTeamMembers"
                                    class="cursor-pointer ml-6 text-sm text-red-500 focus:outline-none"
                                    @click="cancelTeamInvitation(invitation)"
                                >
                                    {{ $t('teams.members.pending.cancel') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </JetActionSection>
        </div>

        <div v-if="team.users.length > 0">
            <JetSectionBorder />

            <!-- Manage Team Members -->
            <JetActionSection class="mt-10 sm:mt-0">
                <template #title>
                    <span style="text-transform: capitalize;">{{ $t('teams.members.title', { team_name: team_name }) }}</span>
                </template>

                <template #description>
                    <p class="mt-1 text-sm text-gray-600" v-html="$t('teams.members.description', { team_name: team_name })"></p>
                    <p class="mt-1 text-sm text-gray-600" v-html="$t('teams.members.info')"></p>
                </template>

                <!-- Team Member List -->
                <template #content>
                    <div class="space-y-6">
                        <div v-for="user in team.users" :key="user.id" class="flex items-center justify-between">

                            <div class="flex items-center">
                                <img class="w-8 h-8 rounded-full" :src="user.profile_photo_url" :alt="user.first_name + ' ' + user.last_name">
                                <div class="ml-4">
                                    {{ user.first_name }} {{ user.last_name }}
                                </div>
                            </div>

                            <div class="flex items-center">
                                <!-- Manage Team Member Role -->
                                <button
                                    v-if="(
                                        // If current logged in user is the team owner OR
                                       // $page.props.team.owner.id == $page.props.loggedInUserId || 
                                        (
                                            // Current user has canAddTeamMembers permissions
                                            userPermissions.canAddTeamMembers 
                                            // And there are available roles
                                            && availableRoles.length 
                                            //  and user ID does not equal the owner ID
                                            //&& user.id != $page.props.team.owner.id 
                                            // and user ID does not equal the current user ID
                                            && user.id != $page.props.auth.user.id
                                            // And the user is currently an admin or a member
                                            && (user.membership.role == 'admin' || user.membership.role == 'member')
                                            )
                                        )"
                                    class="ml-2 text-sm text-gray-400 underline"
                                    @click="manageRole(user)"
                                >
                                    {{ displayableRole(user.membership.role) }}
                                </button>

                                <div v-else-if="availableRoles.length" class="ml-2 text-sm text-gray-400">
                                    {{ displayableRole(user.membership.role) }}
                                </div>

                                <!-- Leave Team -->
                                <button
                                    v-if="$page.props.loggedInUserRole.role == 'admin' 
                                          && adminCount > 1
                                          && $page.props.auth.user.id === user.id
                                          && $page.props.auth.user.current_team.user_id
                                           || 
                                          $page.props.loggedInUserRole.role == 'member'
                                          && $page.props.auth.user.id === user.id
                                          && $page.props.auth.user.current_team.user_id"
                                    class="cursor-pointer ml-6 text-sm text-red-500"
                                    @click="confirmLeavingTeam"
                                >
                                    {{ $t('teams.members.leave') }}
                                </button>

                                <!--
                                <button
                                    v-if="$page.props.auth.user.current_team.user_id == user.id"
                                    class="cursor-pointer ml-6 text-sm text-grey-500"
                                >
                                {{ $t('teams.members.owner') }}
                                </button>
                                -->

                                <!-- Remove Team Member -->
                                <button
                                    v-if="(
                                        //$page.props.team.owner.id == $page.props.loggedInUserId ||
                                        (   
                                            userPermissions.canRemoveTeamMembers //&& user.id != $page.props.team.owner.id
                                            && user.id != $page.props.auth.user.id 
                                            //&& user.membership.role != 'admin'
                                            )
                                        )"
                                    class="cursor-pointer ml-6 text-sm text-red-500"
                                    @click="confirmTeamMemberRemoval(user)"
                                >
                                <span v-if="$page.props.loggedInUserId == user.id">
                                    {{ $t('teams.members.leave') }}
                                </span>
                                <span v-else>
                                    {{ $t('teams.members.remove') }}
                                </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </JetActionSection>
        </div>

        <!-- Role Management Modal -->
        <JetDialogModal :show="currentlyManagingRole" @close="currentlyManagingRole = false">
            <template #title>
                {{ $t('teams.members.role.title') }}
            </template>

            <template #content>
                <div v-if="managingRoleFor">
                    <div class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer">
                        <button
                            v-for="(role, i) in availableRoles"
                            :key="role.key"
                            type="button"
                            class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                            :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i !== Object.keys(availableRoles).length - 1}"
                            @click="updateRoleForm.role = role.key"
                        >
                            <div :class="{'opacity-50': updateRoleForm.role && updateRoleForm.role !== role.key}">
                                <!-- Role Name -->
                                <div class="flex items-center">
                                    <div class="text-sm text-gray-600" :class="{'font-semibold': updateRoleForm.role === role.key}">
                                        {{ role.name }}
                                    </div>

                                    <svg
                                        v-if="updateRoleForm.role === role.key"
                                        class="ml-2 h-5 w-5 text-green-400"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    ><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>

                                <!-- Role Description -->
                                <div class="mt-2 text-xs text-gray-600">
                                    {{ role.description }}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </template>

            <template #footer>
                <JetSecondaryButton @click="currentlyManagingRole = false">
                    {{ $t('teams.members.role.cancel') }}
                </JetSecondaryButton>

                <JetButton
                    class="ml-3"
                    :class="{ 'opacity-25': updateRoleForm.processing }"
                    :disabled="updateRoleForm.processing"
                    @click="updateRole"
                >
                    {{ $t('teams.members.role.save') }}
                </JetButton>
            </template>
        </JetDialogModal>

        <!-- Leave Team Confirmation Modal -->
        <JetConfirmationModal :show="confirmingLeavingTeam" @close="confirmingLeavingTeam = false">
            <template #title>
                {{ $t('teams.members.leave-modal.title') }}
            </template>

            <template #content>
                {{ $t('teams.members.leave-modal.warning') }}
            </template>

            <template #footer>
                <JetSecondaryButton @click="confirmingLeavingTeam = false">
                    {{ $t('teams.members.leave-modal.cancel') }}
                </JetSecondaryButton>

                <JetDangerButton
                    class="ml-3"
                    :class="{ 'opacity-25': leaveTeamForm.processing }"
                    :disabled="leaveTeamForm.processing"
                    @click="leaveTeam"
                >
                {{ $t('teams.members.leave-modal.confirm') }}
                </JetDangerButton>
            </template>
        </JetConfirmationModal>

        <!-- Remove Team Member Confirmation Modal -->
        <JetConfirmationModal :show="teamMemberBeingRemoved" @close="teamMemberBeingRemoved = null">
            <template #title>
                {{ $t('teams.members.remove-modal.title') }}
            </template>

            <template #content>
                {{ $t('teams.members.remove-modal.warning') }}
            </template>

            <template #footer>
                <JetSecondaryButton @click="teamMemberBeingRemoved = null">
                    {{ $t('teams.members.remove-modal.cancel') }}
                </JetSecondaryButton>

                <JetDangerButton
                    class="ml-3"
                    :class="{ 'opacity-25': removeTeamMemberForm.processing }"
                    :disabled="removeTeamMemberForm.processing"
                    @click="removeTeamMember"
                >
                {{ $t('teams.members.remove-modal.confirm') }}
                </JetDangerButton>
            </template>
        </JetConfirmationModal>
    </div>
</template>
